import React, { useEffect, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlusCircle,
  faSortAlt,
  faUndo,
  faSave,
} from '@fortawesome/pro-solid-svg-icons'
import * as fromLessonActions from 'store/actions/lessons-admin.action'
import * as fromCourseActions from 'store/actions/courses.action'
import { IMainState } from 'store/states'
import { ILessonModel, lessonInitialStore } from 'models/lesson.model'
import Loader from 'components/ui/loader/Loader'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { useAccess } from 'contexts/AccessContext'
import { CourseStateType } from 'enum'
import Wrapper from 'components/reusables/wrapper'
import getDc3AttestationAssigned from 'api/administration/courses/GetDc3AttestationAssigned'
import {
  MoveBlocksButton,
  CancelOrderButton,
  SaveOrderButton,
} from '../blocks/components/ai-evaluation/BlocksToolbar.styled'
import LessonList from './components/lessons-list/LessonList'
import { NormativeDc3LessonsAlert } from './components/NormativeDc3LessonsAlert'
import { InactiveCourseLessonsAlert } from './components/InactiveCourseLessonsAlert'
import styles from './Index.module.scss'
import { LessonsFormFooter } from './components/LessonsFormFooter'

const Index: React.FC = () => {
  const { t } = useTranslation()

  const { courseId } = useParams<Record<'courseId', string>>()
  const dispatch = useDispatch()
  const history = useHistory()

  const { course } = useSelector(({ courses }: IMainState) => courses)
  const { loading, showWarning } = useSelector(
    ({ lessons }: IMainState) => lessons
  )

  const [showLoading, setShowLoading] = useState(loading)
  const [order, setOrder] = useState([lessonInitialStore])
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [selected, setSelected] = useState(0)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [draggable, setDraggable] = useState(false)
  const [auxList, setAuxList] = useState([] as any)
  const [lessons, setLessons] = useState([] as any)
  const [contentCount, setContentCount] = useState([])
  const { can, canAccessCourse, loading: accessLoading } = useAccess()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const [hasResponses, setHasResponses] = useState(false)
  const [dc3AttestationAssigned, setDc3AttestationAssigned] = useState<
    boolean | null
  >(null)

  const isImparted = course.state === CourseStateType.Imparted
  const isInactive = course.state === CourseStateType.Inactive
  const lessonsLocked = isImparted || isInactive
  const isDc3AttestationCheckPending =
    attestationsEnabled &&
    course.normative === true &&
    dc3AttestationAssigned === null
  const showNormativeDc3Alert =
    attestationsEnabled &&
    course.normative === true &&
    dc3AttestationAssigned === false

  useEffect(() => {
    if (
      !accessLoading &&
      (!can('manageInternalCourses') || !canAccessCourse(Number(courseId)))
    ) {
      window.location.href = '/forbidden'
    }
  }, [accessLoading, can, canAccessCourse, courseId])

  useEffect(() => {
    setShowLoading(loading)
  }, [loading])

  useEffect(() => {
    return () => {
      setButtonLoading(false)
      dispatch({
        type: fromLessonActions.SHOW_WARNING_LESSON,
        payload: undefined,
      })
    }
  }, [dispatch])

  useEffect(() => {
    dispatch({ type: fromCourseActions.GET_ADMIN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  useEffect(() => {
    const numericCourseId = Number(courseId)

    if (!numericCourseId || course.id !== numericCourseId) {
      return
    }

    if (!attestationsEnabled || !course.normative) {
      setDc3AttestationAssigned(null)
      return
    }

    setDc3AttestationAssigned(null)
    let cancelled = false

    getDc3AttestationAssigned(numericCourseId)
      .then(({ assigned }) => {
        if (!cancelled) {
          setDc3AttestationAssigned(assigned)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDc3AttestationAssigned(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [attestationsEnabled, courseId, course.id, course.normative])

  useEffect(() => {
    if (course.lessons) {
      setAuxList(course.lessons.slice())
      setLessons(course.lessons)
      setContentCount(course.lessonContent)
      setHasResponses(course.hasResponses || false)
    }
  }, [course])

  useEffect(() => {
    if (
      showWarning === undefined &&
      hasResponses &&
      course.id === Number(courseId)
    )
      dispatch({ type: fromLessonActions.SHOW_WARNING_LESSON, payload: true })
  }, [course.id, courseId, dispatch, hasResponses, showWarning])

  const handleSubmit = () => {
    if (order.length > 1)
      dispatch({ type: fromLessonActions.LESSONS_ORDER_EDIT, payload: order })
    setButtonLoading(true)
    history.push('/administration/courses/')
  }

  const handleDelete = (lessonId: number) => {
    setShowDeleteConfirmationModal(false)
    setSelected(0)
    dispatch({ type: fromLessonActions.LESSONS_DESTROY, lessonId, courseId })
  }

  const onDeleteClick = (id: number) => {
    setShowDeleteConfirmationModal(true)
    setSelected(id)
  }

  const handleNewLesson = () => {
    setShowLoading(true)
    // Dispatch con redirect
    dispatch({
      type: fromLessonActions.CREATE_LESSON,
      payload: { courseId, lesson: lessonInitialStore, history },
    })
  }

  const handleCancelDraggable = () => {
    setAuxList(course.lessons.slice())
    setDraggable(false)
  }

  const handleSaveOrder = () => {
    if (order.length > 1)
      dispatch({ type: fromLessonActions.LESSONS_ORDER_EDIT, payload: order })
    setDraggable(false)
  }

  const handlePreview = () => {
    window.open(`/administration/courses/${courseId}/preview`)
  }

  return showLoading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <Row className="pb-4" style={{ margin: '-11px 0 0 0' }}>
        <Breadcrumb separator=">">
          <BreadcrumbItem
            href="/administration/courses"
            text={t('administration.lessons.breadcrum.init')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/edit`}
            text={t('administration.lessons.breadcrum.courseData')}
          />
          <BreadcrumbItem
            text={t('administration.lessons.breadcrum.lessons')}
          />
        </Breadcrumb>
      </Row>
      <Row className={'justify-content-center pb-4'}>
        <Col xs={12} className="pb-5">
          <div className={styles.lessonsContentColumn}>
            <h1 className={styles.courseName}>{course.name}</h1>
            {isInactive ? (
              <InactiveCourseLessonsAlert
                className={styles.inactiveCourseAlert}
              />
            ) : null}
            <div className={styles.sectionHeader}>
              <div className="d-flex justify-content-between align-items-end">
                <div className={styles.sectionHeading}>
                  <h2 className={styles.sectionTitle}>
                    {t('administration.lessons.curseLessons')}
                  </h2>
                  <p className={styles.sectionMeta}>
                    {lessons.length > 0
                      ? course.lessons.length > 1
                        ? `${course.lessons.length} ${t(
                            'administration.lessons.lessonsCount.plural'
                          )}`
                        : `${course.lessons.length} ${t(
                            'administration.lessons.lessonsCount.singular'
                          )}`
                      : t('administration.lessons.lessonsCount.noLessons')}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  {!draggable ? (
                    <MoveBlocksButton
                      variant={ButtonVariant.Secondary}
                      size={ButtonSizes.Small}
                      disabled={lessonsLocked}
                      onClick={() => setDraggable(true)}
                    >
                      <FontAwesomeIcon icon={faSortAlt} />
                      {t('administration.lessons.buttons.moveLessons')}
                    </MoveBlocksButton>
                  ) : (
                    <>
                      <CancelOrderButton
                        outline={true}
                        variant={ButtonVariant.Secondary}
                        size={ButtonSizes.Small}
                        onClick={handleCancelDraggable}
                      >
                        <FontAwesomeIcon icon={faUndo} />
                        {t('administration.lessons.buttons.cancel')}
                      </CancelOrderButton>
                      <SaveOrderButton
                        outline={true}
                        variant={ButtonVariant.Success}
                        size={ButtonSizes.Small}
                        onClick={handleSaveOrder}
                      >
                        <FontAwesomeIcon icon={faSave} />
                        {t('administration.lessons.buttons.saveOrder')}
                      </SaveOrderButton>
                    </>
                  )}
                </div>
              </div>
            </div>
            {course && course.lessons && course.lessons.length > 0 && (
              <LessonList
                lessons={auxList}
                onDelete={onDeleteClick}
                draggable={draggable}
                actionsDisabled={lessonsLocked}
                onOrderChange={(lessons: ILessonModel[]) => setOrder(lessons)}
                contentCount={contentCount}
                onContentCountChange={(content: any) =>
                  setContentCount(content)
                }
              />
            )}
            <Button
              className={styles.addLessonButton}
              disabled={draggable || lessonsLocked}
              onClick={() => handleNewLesson()}
            >
              {t('administration.lessons.new')}
              <FontAwesomeIcon icon={faPlusCircle} />
            </Button>
            {showNormativeDc3Alert ? <NormativeDc3LessonsAlert /> : null}
          </div>
        </Col>
      </Row>
      <div className={styles.actionButtons}>
        <LessonsFormFooter
          draggable={draggable}
          buttonLoading={buttonLoading}
          publishDisabled={lessonsLocked || isDc3AttestationCheckPending}
          shouldConfirmNormativePublish={showNormativeDc3Alert}
          onBack={() =>
            history.push(`/administration/courses/${courseId}/edit`)
          }
          onPreview={handlePreview}
          onSaveDraft={handleSubmit}
        />
      </div>
      <ConfirmationModal
        show={showDeleteConfirmationModal}
        title=""
        text={t('administration.modal.deleteLectionConfirmation')}
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => handleDelete(selected)}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
    </Wrapper>
  )
}

export default Index
